import {
  brandOptions,
  organizations,
  suppliers,
  adminUnits,
  contactPersons,
} from './itemsFixtureData';

/**
 * Real persisted Items rows for the DL-62706 epic ("Bulk edit clear fields: interactions,
 * dependency, apply & audit log") — specifically the `dependency-and-apply-audit.spec.ts` slice's
 * dl-62729 (Testmo #2339515, "Custom Field Clear Sends Clear Payload And Persists") and dl-62730
 * (Testmo #2339512, "Apply Clear Creates Per-Item Log Entry") cases.
 *
 * These must be REAL rows in `itemsData` (not an `apiTransform` GET override) because both cases
 * need a genuine `PUT /api/items` bulk-update + a real refetch to prove the field is actually
 * empty afterward — an ephemeral override freezes the response at a fixed snapshot and can never
 * show a post-Apply refetch. Unlike `styles.ts`, `itemsData` is a plain in-memory array with no
 * `localStorage` persistence (`items.ts` never calls `writeStorage`/`localStorage.setItem` for
 * item rows — only `readApiTransform` reads it) — so a genuine `PUT /api/items` mutation IS
 * visible on an in-session refetch (same MirageJS server instance, same array reference), but
 * would NOT survive a full `page.reload()` (a fresh module instance re-seeds from these builders).
 * The spec must verify persistence via an in-session refetch (closing/reopening the bulk-edit
 * modal, which re-selects the row from the table's already-invalidated query), never
 * `page.reload()`.
 *
 * dl-62728 (Testmo #2339513, Supplier→Supplier Contact dependency) does NOT need a new fixture
 * here — it reuses the existing `bulkEditProbeSupplierEmptyA`/`bulkEditProbeSupplierEmptyB` rows
 * (DL-62326 epic, `items.ts`) via `?search=BulkEditProbe Supplier Empty`, since those are already
 * exactly "two items with no existing supplier" (the only state in which Items' bulk-edit
 * Supplier field is not disabled — see `itemFieldAccess.supplier.bulkEditDisabledWhen:
 * anySelectedHasSupplier`, `features/items/utils/fieldAccess.ts`).
 *
 * "Internal notes" (customFieldId 9, plain Text, optional) is the clearable CF used below —
 * Items' built-in `description` field is `excludeFromBulkEdit: true`
 * (`itemFieldAccess.description`), so "Internal notes" is the closest bulk-editable stand-in,
 * mirroring the identical substitution the sibling DL-62704 epic's PRs made for Styles/Items
 * `description`.
 */

const INTERNAL_NOTES_CF_ID = 9;
const INTERNAL_NOTES_VALUE = 'Seeded internal note for the DL-62706 clear-field suite.';

type ItemCustomFieldEntry = {
  id: number;
  customFieldId: number;
  name: string;
  userDefinedId: string | null;
  type: number;
  value: string | null;
  itemCustomFieldAllowedValueId: number | null;
  customFieldAllowedValueUserDefinedID: string | null;
  isMandatory: boolean;
  isSupplierEditable: boolean;
  internalOnly: boolean;
  maxChar: number | null;
  isMultiLine: boolean;
  allowedDecimals: number;
  isMultipleAllowedValue: boolean;
  parentCustomFieldId: number | null;
  parentCustomFieldName: string | null;
};

const internalNotesCf = (cfEntryId: number, value: string | null): ItemCustomFieldEntry => ({
  id: cfEntryId,
  customFieldId: INTERNAL_NOTES_CF_ID,
  name: 'Internal notes',
  userDefinedId: 'NOTES',
  type: 2,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: false,
  isSupplierEditable: false,
  internalOnly: true,
  maxChar: 500,
  isMultiLine: true,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
});

const baseClearFieldItemRow = (
  id: number,
  name: string,
  userDefinedId: string,
  cfValue: string | null
) => ({
  id,
  name,
  userDefinedId,
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-10T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  defaultQty: 1,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: cfValue == null ? [] : [internalNotesCf(id * 10 + 9, cfValue)],
});

/** dl-62729 (Testmo #2339515) — CF clear sends the clear payload and persists on refetch. */
export const clearFieldCfPersistItemA = baseClearFieldItemRow(
  95101,
  'ClearFieldApply Item CF A',
  'IT-95101',
  INTERNAL_NOTES_VALUE
);
export const clearFieldCfPersistItemB = baseClearFieldItemRow(
  95102,
  'ClearFieldApply Item CF B',
  'IT-95102',
  INTERNAL_NOTES_VALUE
);

/** dl-62730 (Testmo #2339512) — Apply Clear completes for every selected item (D2-observable). */
export const clearFieldAuditItemA = baseClearFieldItemRow(
  95111,
  'ClearFieldApply Item Audit A',
  'IT-95111',
  INTERNAL_NOTES_VALUE
);
export const clearFieldAuditItemB = baseClearFieldItemRow(
  95112,
  'ClearFieldApply Item Audit B',
  'IT-95112',
  INTERNAL_NOTES_VALUE
);

/** All new rows for this spec — spread into `itemsData` in `items.ts`. */
export const clearFieldInteractionItemRows = [
  clearFieldCfPersistItemA,
  clearFieldCfPersistItemB,
  clearFieldAuditItemA,
  clearFieldAuditItemB,
];

export const CLEAR_FIELD_ITEM_CONSTANTS = {
  internalNotesCfId: INTERNAL_NOTES_CF_ID,
  internalNotesValue: INTERNAL_NOTES_VALUE,
};
